'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { Monitor, ScanLine, ShoppingCart, Smartphone, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { TimelineItem, type TimelineItemData } from '@/features/landing/components/timeline-item';
import { useSectionVisibility } from '@/features/landing/hooks/useSectionVisibility';

const timelineData: TimelineItemData[] = [
  {
    id: 1,
    key: 'item1',
    icon: Smartphone,
    color: 'text-slate-500',
    bgColor: 'bg-slate-500/10',
    dotColor: 'bg-slate-500',
    customImage: '/landing/user-order.png',
  },
  {
    id: 2,
    key: 'item2',
    icon: ShoppingCart,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    dotColor: 'bg-orange-500',
    customImage: '/landing/weekly-planing.png',
  },
  {
    id: 3,
    key: 'item3',
    icon: Monitor,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    dotColor: 'bg-emerald-500',
    customImage: '/landing/daily-cooking-overview.png',
  },
  {
    id: 4,
    key: 'item4',
    icon: ScanLine,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    dotColor: 'bg-purple-500',
    customImage: '/landing/qr-code.png',
  },
  {
    id: 5,
    key: 'item5',
    icon: Sparkles,
    color: 'text-green-600',
    bgColor: 'bg-green-600/10',
    dotColor: 'bg-green-600',
    customImage: '/landing/zero-waste.jpeg',
  },
];

export function SolutionSection() {
  const t = useTranslations('landing.solution');
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const { ref: visibilityRef } = useSectionVisibility('solution', { threshold: 0.1 });

  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start center', 'end center'],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div
      id="solution"
      className="w-full bg-background font-sans md:px-10 py-24 border-t border-border"
      ref={(el) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
    >
      {/* Header Section */}
      <div className="max-w-4xl mx-auto text-center mb-20 px-4 md:px-8 lg:px-10">
        <Badge
          variant="outline"
          className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
        >
          {t('badge')}
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
          {t.rich('title', {
            highlight: (chunks) => <span className="text-primary">{chunks}</span>,
          })}
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('subtitle')}</p>
      </div>

      {/* Timeline Section */}
      <div ref={timelineRef} className="relative max-w-7xl mx-auto pb-20 px-4">
        {timelineData.map((item, index) => (
          <TimelineItem
            key={item.id}
            item={item}
            index={index}
            translationNamespace="landing.solution"
            variant="solution"
          />
        ))}

        {/* Timeline Line - Mobile (left) and Desktop (center) */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-border to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)] md:-translate-x-1/2">
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-emerald-500 via-green-500 to-transparent from-[0%] via-[10%] rounded-full"
          />
        </div>
      </div>

      {/* Closing Section - The Real Solution */}
      <div className="max-w-4xl mx-auto mt-16 px-4 md:px-8 lg:px-10">
        <div className="p-8 md:p-12 bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-900">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
            {t('realSolution.title')}
          </h3>
          <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
            {t('realSolution.description1')}
          </p>
          <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
            {t.rich('realSolution.description2', {
              highlight: (chunks) => <strong className="text-foreground">{chunks}</strong>,
            })}
          </p>
          <p className="text-lg text-muted-foreground mb-2 leading-relaxed">
            {t.rich('realSolution.description3', {
              highlight: (chunks) => <strong className="text-foreground">{chunks}</strong>,
            })}
          </p>
          <p className="text-sm italic text-muted-foreground/80 mt-4">{t('realSolution.italic')}</p>
        </div>
      </div>
    </div>
  );
}
